import React, { FC, ReactElement, SyntheticEvent, useCallback, useState } from "react"
import * as O from "fp-ts/Option"
import { FormProvider, SetValueConfig, useForm, UseFormReturn } from "react-hook-form"
import {
    Button,
    ButtonsContainer,
    FormGenericErrorMessage,
    FormRow,
    TextArea,
} from "@silversea-ssc/ui-components-react"
import * as E from "fp-ts/Either"
import * as F from "fp-ts/function"
import { t, Trans } from "@lingui/macro"
import { CustomerInfo } from "@ssc-booking/api-contracts"
import { EType } from "@morphic-ts/summoners"
import { i18n } from "@lingui/core"
import { useGeolocation } from "../../app-core/_common/providers/geolocation-provider"
import FriendlyCaptcha, { defaultCaptchaErrorHandler } from "../FriendlyCaptcha/FriendlyCaptcha"
import { Locale } from "../../app-core/_common/format/localization"
import VisibleInLocal from "../VisibleInLocal"
import { useECommerceMachine, useSwitchFlowMachine } from "../../app-core/_common/machines/machines-context"
import { generateFormTrackingId, getTrackingData } from "../../app-core/features/_common/tracking"
import { useValidateWebQuoteCommand } from "../../app-core/features/_common/machines/ecommerce-machine/data-loading/validate-web-quote-command-machine"
import { webQuoteServerValidationError } from "../../app-core/features/web-quote/utils"
import { isWebQuoteSubmitting } from "../../app-core/features/web-quote/WebQuoteModal/WebQuoteModal"
import FormSectionTitle from "../FormSectionTitle/FormSectionTitle"
import { VisibleMobile } from "../ResponsiveComponents"
import PhoneInputControl from "../Form/PhoneInputControl"
import Form from "../Form/Form"
import InputWithError from "../Form/InputWithError"
import Disclaimer from "../Form/Disclaimer"
import { useReceiptReadModel } from "../../app-core/features/_common/machines/receipt-machine"
import TitleSelect from "../Form/TitleSelect"
import { email, merge, required } from "../Form/form-helpers"
import { InputTextWithError } from "../Form/InputTextWithError"
import { isWebQuote } from "../../app-core/features/_common/machines/ecommerce-machine/e-commerce-machine-transitions"
import styles from "./WebQuoteForm.module.scss"
import { useSetErrors, useUpdateFlowFormOnChange } from "./hooks"

export type WebQuoteFormData = Partial<EType<typeof CustomerInfo>>

type SubmitButton = {
    width?: "full" | "half"
    content?: string | ReactElement
    variant?: "primary" | "accent"
}

type Props = {
    formName: string
    dataContentName?: string
    withComments?: boolean
    submitButton?: SubmitButton
}

const WebQuoteForm: FC<React.PropsWithChildren<Props>> = ({
    formName,
    dataContentName,
    withComments = false,
    submitButton: maybeSubmitButton,
}) => {
    const { countryCode, countryPath } = useGeolocation()

    const [state, send] = useECommerceMachine()
    const [stateSF, sendSF] = useSwitchFlowMachine()
    const [stateWQ] = useValidateWebQuoteCommand(state)
    const [stateRRM] = useReceiptReadModel(state)
    const submitButton: SubmitButton = Object.assign(
        {
            width: "half" as const,
            content: <Trans>Send Quote</Trans>,
            variant: "primary",
        },
        maybeSubmitButton,
    )

    const formData = stateSF.context.webQuoteData
    const isLoading = !stateRRM.matches("present.priceTotal")
    const isSubmitting = isWebQuoteSubmitting(stateWQ)
    const validationError = webQuoteServerValidationError(stateWQ)

    const formMethods = useForm<WebQuoteFormData>({
        mode: "onChange",
        reValidateMode: "onChange",
        shouldUnregister: true,
        shouldFocusError: true,
        criteriaMode: "all",
        defaultValues: O.toUndefined(formData),
    })
    const { formState, register, setError, watch } = formMethods
    const [captchaSolution, setCaptchaSolution] = useState<string>()

    useUpdateFlowFormOnChange(watch, sendSF)

    const onSubmit = useCallback(
        (customer: CustomerInfo, captchaSolution: string) => {
            const trackingData = getTrackingData(generateFormTrackingId())
            console.log("VALIDATE_WEB_QUOTE")
            send({ type: "VALIDATE_WEB_QUOTE", mainGuest: customer, trackingData, captchaSolution })
            window.scrollTo(0, 0) // scroll to top to show the loading message
        },
        [send],
    )
    const _onSubmit = useCallback(
        (data: WebQuoteFormData) => {
            const isValid = formState.isValid
            if (!isValid) {
                return false
            }

            return F.pipe(
                CustomerInfo.strictType.decode(data),
                E.map((customerInfo) => onSubmit(customerInfo, captchaSolution || "")),
                E.fold(F.constFalse, F.constTrue),
            )
        },
        [captchaSolution, formState.isValid, onSubmit],
    )

    useSetErrors(validationError, setError)

    const inputDisabled = isLoading || isSubmitting || isWebQuote(state)

    return (
        <FormProvider {...formMethods}>
            <Form name={formName} onSubmit={_onSubmit} className={styles.form} dataContentName={dataContentName}>
                <VisibleInLocal>
                    <Button dataContentPiece="web_quote_fill" onClick={fillFormWithTestData(formMethods)}>
                        Fill
                    </Button>
                </VisibleInLocal>
                <VisibleMobile>
                    <FormSectionTitle>
                        <Trans>Guest</Trans>
                    </FormSectionTitle>
                </VisibleMobile>
                <FormRow columns="2">
                    <InputWithError name="title" label={`${t`Form Title`} *`}>
                        <TitleSelect
                            {...register("title", required(t`form-required-title`))}
                            defaultValue=""
                            disabled={inputDisabled}
                            autoComplete="honorific-prefix"
                            dataContentPiece="form_title_select"
                        />
                    </InputWithError>
                </FormRow>

                <FormRow columns="2">
                    <InputTextWithError
                        register={register("firstName", required(t`form-required-firstname`))}
                        label={`${t`Form First name`} *`}
                        disabled={inputDisabled}
                        type="text"
                        autoComplete="given-name"
                    />

                    <InputTextWithError
                        register={register("lastName", required(t`form-required-lastname`))}
                        label={`${t`Form Last name`} *`}
                        disabled={inputDisabled}
                        type="text"
                        autoComplete="family-name"
                    />
                </FormRow>

                <InputTextWithError
                    register={register(
                        "email",
                        merge(required(t`form-required-email-address`), email(t`form-invalid-email-address`)),
                    )}
                    label={`${t`Form Email`} *`}
                    disabled={inputDisabled}
                    type="email"
                    autoComplete="email"
                />

                <PhoneInputControl
                    name="phone"
                    defaultValue=""
                    label={`${t`Form Phone`} *`}
                    disabled={inputDisabled}
                    countryCode={countryCode}
                />

                {withComments && (
                    <InputWithError name="comments" label={`${t`Details About Your Request`}`}>
                        <TextArea {...register("comments")} disabled={inputDisabled} />
                    </InputWithError>
                )}

                <div className={styles.requiredFieldsLabel}>
                    *<Trans>Required fields</Trans>
                </div>

                <div className={styles.disclaimerText}>
                    <Disclaimer countryPath={countryPath} content={state.context.sanityContent.formDisclaimer} />

                    <div className={styles.captcha}>
                        <FriendlyCaptcha
                            doneCallback={setCaptchaSolution}
                            errorCallback={defaultCaptchaErrorHandler}
                            locale={i18n.locale as Locale}
                            validationError={validationError === "invalidCaptcha"}
                        />
                    </div>
                </div>

                <div className={styles.footer}>
                    {!formState.isValid && formState.isSubmitted && (
                        <FormGenericErrorMessage message="One or more fields have an error" />
                    )}
                    <ButtonsContainer>
                        <Button
                            className={
                                submitButton.width === "full"
                                    ? styles.bodyFooterButtonFull
                                    : styles.bodyFooterButtonHalf
                            }
                            renderAs="button"
                            type="submit"
                            variant={submitButton.variant}
                            loading={isSubmitting}
                            disabled={inputDisabled || !captchaSolution}
                            data-test-id="submit"
                            dataContentPiece="submit_button"
                        >
                            {submitButton.content}
                        </Button>
                    </ButtonsContainer>
                </div>
            </Form>
        </FormProvider>
    )
}

const fillFormWithTestData = (formMethods: UseFormReturn<WebQuoteFormData>) => (e: SyntheticEvent) => {
    e.preventDefault()
    const { setValue } = formMethods
    const options: SetValueConfig = { shouldValidate: true }

    setValue("title", "mr", options)
    setValue("firstName", "xx_Test", options)
    setValue("lastName", "xx_Test", options)
    setValue("email", "info@doubleloop.io", options)
    setValue("phone", "+393339087668", options)
}

export default WebQuoteForm
